// ===============================
// desiderata_garde_port_royal_pr1.jsx
// ===============================
// Saisie des desiderata pour les gardes "Port Royal 1"
// Rôle :
// - Affiche un tableau par semaines pour un mois configuré
// - Permet à chaque médecin de saisir, pour chaque jour, un niveau de disponibilité
//   (0 = indisponible, 1 = souhaite absolument, 2 = disponible, 3 = peu disponible)
// - Implique une complétion OBLIGATOIRE de tous les jours du mois avant validation
// - Stocke temporairement les choix en localStorage (par médecin et par mois)
// - Envoie les desiderata validés au backend PR1 via POST /api/v2/desiderata-pr1
// - Verrouille la saisie après la date butoir (deadline_iso)
// - ⚙️ Les paramètres du mois sont chargés dynamiquement via GET /api/v2/config/desiderata-pr1
// - ✅ (AJOUT MINIMAL) support multi-mois via cfg.mois_ouverts + sélection du mois
// ===============================

import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "./contexts/AuthContext.jsx";
import LogoutButton from "./components/LogoutButton.jsx";
import { apiFetch, fetchMedecinsMaitre } from "./apiMedecins.js";
import joursFeries from "./joursFeries2025.json";
import { notify } from "./ui/dialogService.js";

// ====== Utilitaires ======
const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const isWeekend = (date) => [0, 6].includes(date.getDay());
const isFerie = (date) =>
  Object.prototype.hasOwnProperty.call(joursFeries, toISO(date));

function isoWeekNumber(date) {
  const tmp = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  tmp.setUTCDate(tmp.getUTCDate() + 4 - ((tmp.getUTCDay() + 6) % 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
}

function getWeeksForMonth(year, monthIdx) {
  const first = new Date(year, monthIdx, 1);
  const last = new Date(year, monthIdx + 1, 0);
  const start = new Date(first);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));

  const weeks = [];
  let cursor = new Date(start);
  while (cursor <= last || cursor.getDay() !== 1) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    if (week.some((d) => d.getMonth() === monthIdx)) weeks.push(week);
    if (cursor.getMonth() > monthIdx && cursor.getDay() === 1) break;
  }
  return weeks;
}

// Tous les jours du mois (en ISO) pour la validation complète des desiderata
function getMonthDaysIso(year, monthIdx) {
  const days = [];
  const first = new Date(year, monthIdx, 1);
  const last = new Date(year, monthIdx + 1, 0);
  for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
    days.push(toISO(d));
  }
  return days;
}

const MOIS_FR = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

function labelFromMoisCle(moisCle) {
  const m = String(moisCle || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return String(moisCle || "");
  const y = Number(m[1]);
  const mi = Number(m[2]) - 1;
  if (!Number.isFinite(y) || !Number.isFinite(mi) || mi < 0 || mi > 11) return String(moisCle || "");
  return `${MOIS_FR[mi]} ${y}`;
}

function labelFromCode(code) {
  const opt = GARDE_OPTIONS.find((o) => o.code === code);
  return opt ? opt.label : "✅ Disponible";
}

function convertLegacyStatusToCode(statutRaw) {
  const s = String(statutRaw || "").trim().toLowerCase();
  if (!s) return 2;
  if (s === "souhaite" || s === "souhaite-absolument" || s === "prioritaire") return 1;
  if (s === "peu-disponible" || s === "peu_disponible" || s === "possible") return 3;
  if (
    s === "indisponible" ||
    s === "ca-rtt" ||
    s === "ca" ||
    s === "rtt" ||
    s === "cf" ||
    s === "formation" ||
    s === "absence"
  ) {
    return 0;
  }
  return 2;
}

function legacyValuesToPr1Values(rawValeurs, moisCle) {
  const jours = rawValeurs?.jours;
  if (!jours || typeof jours !== "object") return {};
  const out = {};
  for (const [iso, day] of Object.entries(jours)) {
    if (!String(iso).startsWith(`${moisCle}-`)) continue;
    const amCode = convertLegacyStatusToCode(day?.am?.statut);
    const pmCode = convertLegacyStatusToCode(day?.pm?.statut);
    // Règle prudente : le niveau le plus contraignant de la journée l'emporte
    const dayCode = Math.min(amCode, pmCode);
    out[iso] = { code: dayCode, label: labelFromCode(dayCode) };
  }
  return out;
}

// ====== Styles ======
const styles = {
  container: {
    maxWidth: "1200px",
    margin: "24px auto",
    padding: "24px",
    background: "#f7f9fc",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
    fontFamily:
      "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: "16px",
  },
  title: { fontSize: "32px", fontWeight: 700, margin: 0, color: "#1f2937" },
  subTitle: { fontSize: "23px", color: "#4b5563", marginTop: "4px" },
  legend: {
    fontSize: "16px",
    color: "#374151",
    marginTop: "8px",
    marginBottom: "12px",
  },
  select: {
    minWidth: "340px",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: "20px",
    outline: "none",
  },
  numberInput: {
    width: "90px",
    padding: "8px",
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
    fontSize: "18px",
  },
  pill: (variant = "neutral") => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    borderRadius: "9999px",
    fontSize: "20px",
    fontWeight: 600,
    background:
      variant === "locked"
        ? "rgba(220, 38, 38, 0.10)"
        : "rgba(59, 130, 246, 0.10)",
    color: variant === "locked" ? "#b91c1c" : "#1d4ed8",
    border:
      variant === "locked"
        ? "1px solid rgba(220,38,38,0.25)"
        : "1px solid rgba(59,130,246,0.25)",
    whiteSpace: "nowrap",
  }),
  tableWrap: {
    width: "100%",
    overflowX: "auto",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    tableLayout: "fixed",
    fontSize: "23px",
  },
  th: {
    position: "sticky",
    top: 0,
    background: "#f3f4f6",
    color: "#111827",
    fontWeight: 700,
    borderBottom: "1px solid #e5e7eb",
    padding: "10px 8px",
    textAlign: "center",
  },
  td: {
    borderBottom: "1px solid #f1f5f9",
    padding: "6px",
    textAlign: "center",
    verticalAlign: "middle",
  },
  dayCell: (isWe, isFer) => ({
    borderRight: "1px solid #f1f5f9",
    background: isFer
      ? "linear-gradient(180deg, #fef2f2, #fee2e2)" // Rouge clair pour jours fériés
      : isWe
      ? "linear-gradient(180deg, #fef2f2, #fee2e2)" // Rouge clair pour weekends (samedi/dimanche)
      : "#fff",
  }),
  dayHeader: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#374151",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
  },
  dateBadge: {
    fontSize: "20px",
    color: "#6b7280",
    background: "#eef2ff",
    border: "1px solid #e5e7eb",
    padding: "2px 8px",
    borderRadius: "9999px",
  },
  // 🔴 Badge rouge pour dimanches et jours fériés
  dateBadgeRed: {
    fontSize: "20px",
    color: "#dc2626",
    fontWeight: 700,
    background: "#fee2e2",
    border: "1px solid #fecaca",
    padding: "2px 8px",
    borderRadius: "9999px",
  },
  // 🔴 Nom du jour en rouge pour dimanches et jours fériés
  dayNameRed: {
    color: "#dc2626",
    fontWeight: 700,
  },
  ferBadge: {
    fontSize: "11px",
    color: "#b45309",
    background: "#ffedd5",
    border: "1px solid #fed7aa",
    padding: "2px 6px",
    borderRadius: "9999px",
    marginLeft: "6px",
  },
  selectDispo: {
    width: "100%",
    padding: "6px 8px",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: "13px",
    outline: "none",
  },
  footer: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: "16px",
  },
  btn: (variant = "primary", disabled = false) => ({
    padding: "10px 14px",
    borderRadius: "10px",
    border: "1px solid transparent",
    fontWeight: 700,
    fontSize: "14px",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    background: variant === "primary" ? "#111827" : "transparent",
    color: variant === "primary" ? "#fff" : "#111827",
    borderColor: variant === "outline" ? "#d1d5db" : "transparent",
  }),
  note: { fontSize: "12px", color: "#6b7280" },
};

// ====== Options gardes ======
const GARDE_OPTIONS = [
  { code: 0, label: "🚫 Indisponible" },
  { code: 3, label: "😕 Peu disponible" },
  { code: 2, label: "✅ Disponible" },
  { code: 1, label: "⭐ Souhaite absolument" },
];

// ====== Defaults (fallback si config backend indisponible) ======
const DEFAULT_CFG = {
  annee: 2026,
  mois_index: 1, // février
  mois_label: "Février 2026",
  mois_cle: "2026-02",
  deadline_iso: "2026-01-31",
  deadline_affichage: "31/01/2026",
  // ✅ support multi-mois : optionnel
  mois_ouverts: null,
};

// ====== Composant principal ======
const DesiderataGardePortRoyal = () => {
  const { user, isAuthenticated } = useAuth();
  const [medecins, setMedecins] = useState([]);
  const [values, setValues] = useState({});
  const [nbGardes, setNbGardes] = useState(""); // Vide par défaut pour forcer la saisie
  const [nowIso] = useState(() => toISO(new Date()));

  // Pop-up d'introduction
  const [showIntro, setShowIntro] = useState(true);
  // Pop-up pour désidératas non ouverts
  const [showNotOpen, setShowNotOpen] = useState(false);
  
  // Vérification de participation
  const [isParticipating, setIsParticipating] = useState(null); // null = vérification en cours
  const [showParticipationModal, setShowParticipationModal] = useState(false);
  
  // Médecin connecté (dérivé de user)
  const selectedMedecinKey = useMemo(() => {
    // Si on a user.nom et user.prenom, on peut les utiliser directement
    if (user?.nom && user?.prenom) {
      return `${user.nom}_${user.prenom}`;
    }
    // Sinon, chercher dans la liste des médecins par medecin_id
    if (!user?.medecin_id || !medecins.length) return "";
    const medecin = medecins.find((m) => m.medecin_id === user.medecin_id);
    if (!medecin) return "";
    return `${medecin.nom}_${medecin.prenom}`;
  }, [user, medecins]);

  const normalizeText = (str) =>
    String(str || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  // ⚙️ Config dynamique (backend)
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [cfgError, setCfgError] = useState(null);

  // ✅ (AJOUT MINIMAL) mois sélectionné côté médecin (mois_cle)
  const [selectedMoisCle, setSelectedMoisCle] = useState("");

  useEffect(() => {
    const loadCfg = async () => {
      try {
        setCfgLoading(true);
        setCfgError(null);

        const res = await apiFetch("/api/v2/config/desiderata-pr1");
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} lecture config. ${t}`);
        }

        const json = (await res.json()) || {};
        console.log('✅ [PR1] Config chargée:', { 
          mois_ouverts: json.mois_ouverts?.length || 0,
          mois_ouverts_array: json.mois_ouverts,
          mois_cle: json.mois_cle,
          annee: json.annee,
          mois_index: json.mois_index
        });
        setCfg((prev) => ({ ...prev, ...json }));
      } catch (e) {
        console.error("❌ Erreur chargement config desiderata :", e);
        setCfgError(e.message || "Erreur inconnue");
        setCfg(DEFAULT_CFG); // fallback
      } finally {
        setCfgLoading(false);
      }
    };

    loadCfg();
  }, []);

  // ✅ Liste des mois ouverts = exactement celle configurée par le Responsable
  // Si mois_ouverts est un tableau vide => désidératas fermés
  // Le fallback legacy n'est utilisé que si mois_ouverts n'existe pas du tout
  const moisOuverts = useMemo(() => {
    if (Array.isArray(cfg?.mois_ouverts)) {
      // mois_ouverts existe (même vide) => l'utiliser directement
      return cfg.mois_ouverts;
    } else if (cfg?.mois_cle) {
      // mois_ouverts n'existe pas du tout => fallback legacy (ancienne config)
      return [
        {
          annee: cfg.annee,
          mois_index: cfg.mois_index,
          mois_label: cfg.mois_label,
          mois_cle: cfg.mois_cle,
          deadline_iso: cfg.deadline_iso,
          deadline_affichage: cfg.deadline_affichage,
        },
      ];
    }
    return [];
  }, [cfg]);

  // Historique PR1 volontairement désactivé : accès uniquement aux mois ouverts.
  const [moisHistorique] = useState([]);
  const [moisValidesHistorique] = useState([]);

  const moisConsultables = useMemo(() => {
    return [...moisOuverts]
      .map((m) => ({
        ...m,
        mois_cle: String(m?.mois_cle || ""),
        historyOnly: false,
      }))
      .filter((m) => /^\d{4}-\d{2}$/.test(m.mois_cle))
      .sort((a, b) => String(b.mois_cle).localeCompare(String(a.mois_cle)));
  }, [moisOuverts]);

  const preferredDefaultMoisCle = useMemo(() => {
    if (moisConsultables.length === 0) return "";
    if (
      cfg?.mois_cle &&
      moisConsultables.some((m) => String(m.mois_cle) === String(cfg.mois_cle))
    ) {
      return String(cfg.mois_cle);
    }
    return String(moisConsultables[0].mois_cle);
  }, [moisConsultables, cfg]);

  // ✅ (AJOUT MINIMAL) initialiser le mois sélectionné (par défaut = mois principal)
  useEffect(() => {
    if (moisConsultables.length > 0) {
      const moisCleValide = moisConsultables.find(
        (m) => String(m.mois_cle) === String(selectedMoisCle)
      );
      if (selectedMoisCle && !moisCleValide) {
        setSelectedMoisCle(preferredDefaultMoisCle);
        return;
      }
      if (!selectedMoisCle) {
        setSelectedMoisCle(preferredDefaultMoisCle);
        return;
      }
    } else if (!selectedMoisCle && cfg?.mois_cle) {
      setSelectedMoisCle(String(cfg.mois_cle));
    }
  }, [cfg, moisConsultables, selectedMoisCle, preferredDefaultMoisCle]);

  const selectedMonth = useMemo(() => {
    if (!selectedMoisCle) return null;
    return (
      moisConsultables.find((m) => String(m.mois_cle) === String(selectedMoisCle)) ||
      null
    );
  }, [moisConsultables, selectedMoisCle]);

  // ====== Paramètres (issus du mois sélectionné) ======
  const YEAR = Number(selectedMonth?.annee);
  const MONTH_INDEX = Number(selectedMonth?.mois_index); // JS: 0=Janv, 1=Fév, ...
  const DEADLINE_ISO = String(selectedMonth?.deadline_iso || "");
  const MOIS_LABEL = String(selectedMonth?.mois_label || "");
  const DATE_LIMITE_DESIDERATA = String(selectedMonth?.deadline_affichage || "");
  const MOIS_CLE = String(selectedMonth?.mois_cle || "");

  // 🔍 Vérification de la participation aux gardes
  useEffect(() => {
    const checkParticipation = async () => {
      if (!user?.nom || !user?.prenom) {
        console.log("⏳ [GARDES] En attente des données utilisateur...");
        return; // Attendre que les données soient chargées
      }

      console.log("🔍 [GARDES] Vérification participation pour:", user.nom, user.prenom);

      try {
        const res = await apiFetch("/api/v2/medecins-gardes-pr1");
        if (res.ok) {
          const medecinsGardes = await res.json();
          console.log("📋 [GARDES] Liste médecins participants:", medecinsGardes.length);
          
          // Normalisation pour la comparaison (trim, lowercase, suppression accents)
          const normalize = (str) => {
            return String(str || "")
              .trim()
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, ""); // Suppression des accents
          };
          
          const userNomNorm = normalize(user.nom);
          const userPrenomNorm = normalize(user.prenom);
          
          const isInList = medecinsGardes.some((m) => {
            const mNomNorm = normalize(m.nom);
            const mPrenomNorm = normalize(m.prenom);
            const match = mNomNorm === userNomNorm && mPrenomNorm === userPrenomNorm;
            if (match) {
              console.log("✅ [GARDES] Médecin trouvé dans la liste:", m.nom, m.prenom);
            }
            return match;
          });
          
          console.log("🎯 [GARDES] Résultat vérification:", isInList ? "PARTICIPE" : "NE PARTICIPE PAS");
          setIsParticipating(isInList);
          
          // Ne montrer le modal QUE si on est sûr que le médecin ne participe pas
          if (!isInList) {
            console.log("⚠️ [GARDES] Affichage modal de participation requise");
            setShowParticipationModal(true);
          } else {
            setShowParticipationModal(false);
          }
        } else {
          console.warn("⚠️ [GARDES] Erreur HTTP:", res.status);
          // En cas d'erreur HTTP, on ne bloque pas l'accès (on assume que c'est temporaire)
          setIsParticipating(null); // null = incertain, on ne bloque pas
          setShowParticipationModal(false);
        }
      } catch (err) {
        console.error("❌ [GARDES] Erreur vérification participation:", err);
        // En cas d'erreur, on ne bloque pas l'accès (on assume que c'est temporaire)
        setIsParticipating(null); // null = incertain, on ne bloque pas
        setShowParticipationModal(false);
      }
    };

    checkParticipation();
  }, [user]);

  // 🔗 Chargement des médecins depuis SQLite API v2
  useEffect(() => {
    fetchMedecinsMaitre()
      .then((data) => setMedecins(data))
      .catch((err) =>
        console.error("❌ Erreur chargement médecins (PR1) :", err)
      );
  }, []);

  const weeks = useMemo(() => {
    if (!Number.isFinite(YEAR) || !Number.isFinite(MONTH_INDEX)) return [];
    return getWeeksForMonth(YEAR, MONTH_INDEX);
  }, [YEAR, MONTH_INDEX]);

  const monthDaysIso = useMemo(() => {
    if (!Number.isFinite(YEAR) || !Number.isFinite(MONTH_INDEX)) return [];
    return getMonthDaysIso(YEAR, MONTH_INDEX);
  }, [YEAR, MONTH_INDEX]);

  const isHistoryReadOnly = selectedMonth?.historyOnly === true;

  const isLocked = useMemo(() => {
    if (isHistoryReadOnly) return true;
    if (!DEADLINE_ISO) return false;
    return new Date(nowIso) > new Date(DEADLINE_ISO);
  }, [nowIso, DEADLINE_ISO, isHistoryReadOnly]);

  const weekNumbers = useMemo(
    () => weeks.map((w) => isoWeekNumber(w[0])),
    [weeks]
  );

  const storageKey = useMemo(
    () =>
      selectedMedecinKey && MOIS_CLE
        ? `pr1-gardes:${selectedMedecinKey}:${MOIS_CLE}`
        : null,
    [selectedMedecinKey, MOIS_CLE]
  );

  const [dataLoaded, setDataLoaded] = useState(false);
  const [hasMonthData, setHasMonthData] = useState(false);
  const [dataSource, setDataSource] = useState("none");

  const findServerDesiderataForSelectedMedecin = (serverData) => {
    if (!Array.isArray(serverData) || !selectedMedecinKey) return null;
    const [expectedNom, expectedPrenom] = String(selectedMedecinKey || "").split("_");

    return (
      serverData.find(
        (d) =>
          normalizeText(d?.nom) === normalizeText(expectedNom) &&
          normalizeText(d?.prenom) === normalizeText(expectedPrenom)
      ) || null
    );
  };

  const normalizeServerPr1Values = (entry) => {
    const isFlatPr1 =
      entry?.valeurs &&
      Object.keys(entry.valeurs).some((k) => /^\d{4}-\d{2}-\d{2}$/.test(k));
    return isFlatPr1
      ? entry?.valeurs || {}
      : legacyValuesToPr1Values(entry?.valeurs, MOIS_CLE);
  };

  const applyServerDesiderata = (entry) => {
    const normalizedValues = normalizeServerPr1Values(entry);
    if (!normalizedValues || Object.keys(normalizedValues).length === 0) {
      return false;
    }

    setValues(normalizedValues);
    if (typeof entry.nbGardes === "number" && entry.nbGardes > 0) {
      setNbGardes(entry.nbGardes);
    }
    if (storageKey) {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ nbGardes: entry.nbGardes || 0, souhaits: normalizedValues })
      );
    }
    setHasMonthData(true);
    setDataSource(
      entry?.valeurs &&
        Object.keys(entry.valeurs).some((k) => /^\d{4}-\d{2}-\d{2}$/.test(k))
        ? "server"
        : "legacy"
    );
    return true;
  };

  useEffect(() => {
    if (!storageKey || !MOIS_CLE) return;

    setValues({});
    setNbGardes("");
    setDataLoaded(false);
    setHasMonthData(false);
    setDataSource("none");

    const loadDesiderata = async () => {
      let serverReadOk = false;
      try {
        const res = await apiFetch(`/api/v2/desiderata-pr1/${MOIS_CLE}`, {
          credentials: "include",
          cache: "no-store",
        });
        if (res.ok) {
          serverReadOk = true;
          const serverData = await res.json();
          if (Array.isArray(serverData) && serverData.length > 0) {
            const myData = findServerDesiderataForSelectedMedecin(serverData);
            if (applyServerDesiderata(myData)) {
              setDataLoaded(true);
              return;
            }
          }
        }
      } catch (err) {
        console.warn("⚠️ Impossible de charger les desiderata PR1 depuis le serveur, fallback localStorage:", err);
      }

      if (serverReadOk) {
        setDataLoaded(true);
        return;
      }

      const raw = localStorage.getItem(storageKey);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          setValues(parsed.souhaits || {});
          if (typeof parsed.nbGardes === "number" && parsed.nbGardes > 0) {
            setNbGardes(parsed.nbGardes);
          }
          const hasLocalValues =
            parsed?.souhaits && Object.keys(parsed.souhaits || {}).length > 0;
          if (hasLocalValues) {
            setHasMonthData(true);
            setDataSource("local");
          }
        } catch (_) {}
      }
      setDataLoaded(true);
    };

    loadDesiderata();
  }, [storageKey, MOIS_CLE, selectedMedecinKey]);

  useEffect(() => {
    if (storageKey && dataLoaded)
      localStorage.setItem(
        storageKey,
        JSON.stringify({ nbGardes, souhaits: values })
      );
  }, [storageKey, nbGardes, values, dataLoaded]);

  const fmtDay = new Intl.DateTimeFormat("fr-FR", { weekday: "long" });
  const fmtDayNum = new Intl.DateTimeFormat("fr-FR", { day: "2-digit" });

  // Tous les jours du mois doivent avoir un code 0–3
  const hasMissingDays = useMemo(() => {
    if (!selectedMedecinKey) return true;
    return monthDaysIso.some((iso) => {
      const code = values[iso]?.code;
      return !(code === 0 || code === 1 || code === 2 || code === 3);
    });
  }, [monthDaysIso, values, selectedMedecinKey]);

  // --- Gestion des changements de disponibilité ---
  const handleChange = (iso, value) => {
    if (isLocked || !selectedMedecinKey) return;

    const numericCode = parseInt(value, 10);
    const opt = GARDE_OPTIONS.find((o) => o.code === numericCode);

    setValues((prev) => ({
      ...prev,
      [iso]: opt ? { code: numericCode, label: opt.label } : null,
    }));
  };

  // --- Validation (sauvegarde sur le serveur PR1 via /api/v2/desiderata-pr1) ---
  const handleValidate = async () => {
    if (isHistoryReadOnly) {
      notify("Mois historique : consultation en lecture seule uniquement.");
      return;
    }
    if (isLocked) {
      notify(
        "Date butoir dépassée : la modification des desiderata n’est plus possible."
      );
      return;
    }

    const erreurs = [];

    if (!MOIS_CLE) {
      erreurs.push("• sélectionner un mois (configuration invalide)");
    }

    if (!selectedMedecinKey) {
      erreurs.push("• sélectionner votre nom dans la liste déroulante");
    }

    const nb = parseInt(nbGardes, 10);
    if (!nb || nb <= 0) {
      erreurs.push("• indiquer le nombre de gardes souhaité pour le mois");
    }

    if (hasMissingDays) {
      erreurs.push(
        "• renseigner chaque jour du mois avec l’une des 4 options (⭐/✅/😕/🚫)"
      );
    }

    if (erreurs.length > 0) {
      notify(
        [
          "Vous ne pouvez pas encore valider vos desiderata.",
          "",
          "Pour valider, vous devez :",
          "",
          ...erreurs,
          "",
          "Merci de compléter ces éléments puis de relancer la validation.",
        ].join("\n")
      );
      return;
    }

    const [nom, prenom] = selectedMedecinKey.split("_");
    const medecin = { nom, prenom };

    try {
      const response = await apiFetch("/api/v2/desiderata-pr1", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          mois: MOIS_CLE,
          medecin,
          souhaits: { nbGardes: nb, valeurs: values },
        }),
      });

      if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

      const verifyRes = await apiFetch(`/api/v2/desiderata-pr1/${MOIS_CLE}`, {
        credentials: "include",
        cache: "no-store",
      });
      if (!verifyRes.ok) {
        throw new Error(`Sauvegarde non vérifiable (HTTP ${verifyRes.status})`);
      }

      const verifyData = await verifyRes.json();
      const savedEntry = findServerDesiderataForSelectedMedecin(verifyData);
      if (!applyServerDesiderata(savedEntry)) {
        throw new Error("Sauvegarde non retrouvée à la relecture serveur");
      }

      notify(
        [
          "✅ Vos desiderata ont été validés et sauvegardés sur le serveur.",
          "",
          `Vous pouvez les modifier jusqu'à la date limite fixée au ${DATE_LIMITE_DESIDERATA}.`,
        ].join("\n")
      );
    } catch (err) {
      console.error("❌ Erreur sauvegarde désidérata (PR1) :", err);
      notify(
        "❌ Erreur lors de la sauvegarde sur le serveur. Merci de réessayer ultérieurement."
      );
    }
  };

  // Afficher "désidératas non ouverts" uniquement s'il n'y a aucun mois configuré (aligné sur l'interface Responsable)
  useEffect(() => {
    if (cfgLoading) {
      setShowNotOpen(false);
      return;
    }
    const shouldShow = moisOuverts.length === 0;
    if (shouldShow) {
      setShowNotOpen(true);
      setShowIntro(false);
    } else {
      setShowNotOpen(false);
    }
  }, [cfgLoading, moisOuverts.length]);

  // ⚠️ Affichage "chargement config" (léger, pour éviter un écran cassé)
  if (cfgLoading) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Désiderata de garde PR1</h1>
        <p style={{ color: "#6b7280" }}>Chargement de la configuration…</p>
      </div>
    );
  }

  // Modal de participation requise - Bloque l'accès si non participant
  // On n'affiche le modal QUE si :
  // 1. showParticipationModal est explicitement true (après vérification)
  // 2. ET isParticipating est false (pas null, pas undefined)
  // 3. ET on a les données utilisateur
  if (showParticipationModal && isParticipating === false && user?.nom && user?.prenom) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #eef2ff 0%, #f9fafb 40%, #ecfeff 100%)",
          padding: "24px",
        }}
      >
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 700,
                marginBottom: "16px",
                color: "#111827",
              }}
            >
              Participation requise
            </h2>
            <p
              style={{
                fontSize: "16px",
                lineHeight: 1.6,
                color: "#374151",
                marginBottom: "24px",
              }}
            >
              Si vous souhaitez participer à cette activité, merci de cocher la case "Je participe à cette activité" sur la page Espace médecins.
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                onClick={() => window.location.href = "/portail-medecin"}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "1px solid #111827",
                  background: "#111827",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Retour à l'Espace médecins
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modal pour désidératas non ouverts - Bloque l'accès si les désidératas ne sont pas ouverts
  if (showNotOpen) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #eef2ff 0%, #f9fafb 40%, #ecfeff 100%)",
          padding: "24px",
        }}
      >
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>⏳</div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 700,
                marginBottom: "16px",
                color: "#111827",
              }}
            >
              Les désidératas ne sont pas encore ouverts
            </h2>
            <p
              style={{
                fontSize: "16px",
                lineHeight: 1.6,
                color: "#374151",
                marginBottom: "24px",
              }}
            >
              Les désidératas de garde ne sont pas encore ouverts pour le moment.
              <br />
              <br />
              Veuillez patienter jusqu'à ce que le responsable ouvre la période de saisie des désidératas.
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                onClick={() => window.location.href = "/portail-medecin"}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "1px solid #111827",
                  background: "#111827",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Retour à l'Espace médecins
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Pop-up d'introduction affichée à l'ouverture de la page Désiderata (seulement si les désidératas sont ouverts) */}
      {showIntro && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
        >
          <div
            style={{
              background: "#ffffff",
              padding: "24px",
              borderRadius: "16px",
              maxWidth: "520px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            }}
          >
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
              ✍️ Comment remplir vos desiderata de garde PR1
            </h2>
            <ol style={{ paddingLeft: 20, marginBottom: 16, lineHeight: 1.5 }}>
              <li>
                <strong>1️⃣ Choisissez le mois</strong> (si plusieurs mois sont ouverts).
              </li>
              <li>
                <strong>2️⃣ Identifiez-vous</strong> en choisissant votre nom dans la
                liste déroulante en haut de la page.
              </li>
              <li>
                <strong>3️⃣ Indiquez le nombre de gardes</strong> que vous souhaitez
                réaliser pour le mois (champ « Nombre de gardes souhaité »).
              </li>
              <li>
                <strong>4️⃣ Renseignez chaque jour du mois</strong> en choisissant l’une
                des 4 options&nbsp;:
                <br />
                – ⭐ Souhaite absolument<br />
                – ✅ Disponible<br />
                – 😕 Peu disponible<br />
                – 🚫 Indisponible
              </li>
            </ol>
            <p style={{ fontSize: 14, marginBottom: 16 }}>
              Une fois tous les jours renseignés et le nombre de gardes indiqué,
              vous pourrez valider vos desiderata pour les transmettre au responsable.
            </p>

            {cfgError ? (
              <p style={{ fontSize: 13, color: "#b91c1c", marginBottom: 12 }}>
                ⚠️ Config non disponible : utilisation d’une config par défaut.
              </p>
            ) : null}

            <div style={{ textAlign: "right" }}>
              <button
                type="button"
                onClick={() => setShowIntro(false)}
                style={{
                  padding: "8px 16px",
                  borderRadius: 10,
                  border: "1px solid #111827",
                  background: "#111827",
                  color: "#ffffff",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Compris
              </button>
            </div>
          </div>
        </div>
      )}

      {/* en-tête */}
      <div style={styles.header}>
        <div style={{ flex: 1 }}>
          <h1 style={styles.title}>Désiderata de garde PR1 – {MOIS_LABEL}</h1>
          <div style={styles.subTitle}>
            Merci de renseigner vos disponibilités et votre nombre de gardes
            souhaitées.
          </div>
          <div style={styles.legend}>
            💡 Légende : ⭐ Souhaite absolument | ✅ Disponible | 😕 Peu dispo | 🚫 Indisponible
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end" }}>
          <span style={styles.pill(isLocked ? "locked" : "neutral")}>
            {isHistoryReadOnly
              ? "Historique (lecture seule)"
              : isLocked
              ? "Verrouillé (date butoir passée)"
              : `Ouvert jusqu'au ${DATE_LIMITE_DESIDERATA}`}
          </span>
          <LogoutButton />
        </div>
      </div>

      {isHistoryReadOnly && (
        <div
          style={{
            marginBottom: 12,
            background: "#fff7ed",
            border: "1px solid #fdba74",
            color: "#9a3412",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          👁️ Vous consultez un mois historique : modification désactivée.
        </div>
      )}

      {dataLoaded && selectedMedecinKey && MOIS_CLE && !hasMonthData && (
        <div
          style={{
            marginBottom: 12,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          ⚠️ Aucun désidérata PR1 trouvé pour ce mois pour ce compte médecin.
        </div>
      )}

      {dataLoaded && hasMonthData && dataSource === "local" && (
        <div
          style={{
            marginBottom: 12,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1e40af",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 13,
          }}
        >
          ℹ️ Affichage depuis la sauvegarde locale du navigateur.
        </div>
      )}

      {dataLoaded && hasMonthData && dataSource === "legacy" && (
        <div
          style={{
            marginBottom: 12,
            background: "#f0fdf4",
            border: "1px solid #86efac",
            color: "#166534",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 13,
          }}
        >
          ℹ️ Affichage depuis l&apos;historique (ancien format), converti en lecture PR1.
        </div>
      )}

      {/* sélection du mois (ouverts + historique lecture seule) */}
      {(() => {
        const shouldShowSelector = moisConsultables.length > 1;
        if (!shouldShowSelector) return null;

        return (
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12, padding: "12px", background: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
            <label style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
              Mois :
            </label>
            <select
              value={selectedMoisCle}
              onChange={(e) => {
                setSelectedMoisCle(e.target.value);
              }}
              style={styles.select}
            >
              {moisConsultables.map((m) => (
                <option key={String(m.mois_cle)} value={String(m.mois_cle)}>
                  {m.mois_label} ({m.mois_cle}){m.historyOnly ? " — historique" : ""}
                </option>
              ))}
            </select>
            {moisHistorique.length > 0 && (
              <span style={{ color: "#6b7280", fontSize: 13 }}>
                Les mois historiques sont consultables en lecture seule.
              </span>
            )}
          </div>
        );
      })()}

      {/* sélection médecin */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        {selectedMedecinKey ? (
          <div
            style={{
              padding: "12px",
              background: "#f0f9ff",
              borderRadius: "10px",
              border: "1px solid #bae6fd",
              flex: 1,
            }}
          >
            <strong style={{ fontSize: "18px", color: "#0369a1" }}>
              👤 Médecin : {selectedMedecinKey.replace("_", " ")}
            </strong>
          </div>
        ) : (
          <div
            style={{
              padding: "12px",
              background: "#fef2f2",
              borderRadius: "10px",
              border: "1px solid #fecaca",
              color: "#991b1b",
              flex: 1,
            }}
          >
            ⚠️ Aucun médecin associé à votre compte. Contactez l'administrateur.
          </div>
        )}

        <input
          type="number"
          min="0"
          max="10"
          step="1"
          value={nbGardes || ""}
          onChange={(e) => {
            const rawVal = e.target.value;
            if (rawVal === "") {
              setNbGardes(""); // Garder vide si l'utilisateur efface
            } else {
              const val = parseInt(rawVal, 10);
              setNbGardes(isNaN(val) ? "" : val);
            }
          }}
          style={styles.numberInput}
          disabled={isLocked || !selectedMedecinKey}
        />
        <span>gardes souhaitées</span>
      </div>

      {/* tableau par semaines */}
      {weeks.map((week, wIdx) => (
        <div key={wIdx} style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              margin: "6px 6px 8px",
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
              Semaine {weekNumbers[wIdx]}
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {week.map((d) => {
                    const iso = toISO(d);
                    const fer = isFerie(d);
                    const isDimanche = d.getDay() === 0;
                    const isSpecial = fer || isDimanche; // Dimanche ou jour férié
                    const dayName =
                      fmtDay.format(d).charAt(0).toUpperCase() +
                      fmtDay.format(d).slice(1);
                    return (
                      <th key={iso} style={styles.th}>
                        <div style={styles.dayHeader}>
                          <span style={isSpecial ? styles.dayNameRed : undefined}>{dayName}</span>
                          <div>
                            <span style={isSpecial ? styles.dateBadgeRed : styles.dateBadge}>
                              {fmtDayNum.format(d)}
                            </span>
                            {fer && (
                              <span style={styles.ferBadge}>
                                {joursFeries[iso] || "Férié"}
                              </span>
                            )}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {week.map((d) => {
                    const iso = toISO(d);
                    const fer = isFerie(d);
                    const inMonth = d.getMonth() === MONTH_INDEX;
                    return (
                      <td
                        key={iso}
                        style={{
                          ...styles.td,
                          ...styles.dayCell(isWeekend(d), fer),
                        }}
                      >
                        {inMonth ? (
                          <select
                            style={styles.selectDispo}
                            value={values[iso]?.code ?? ""}
                            onChange={(e) => handleChange(iso, e.target.value)}
                            disabled={isLocked || !selectedMedecinKey}
                          >
                            <option value="">— choisir —</option>
                            {GARDE_OPTIONS.map((opt) => (
                              <option key={opt.code} value={opt.code}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span style={{ color: "#9ca3af", fontSize: 12 }}>
                            Hors mois
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* footer */}
      <div style={styles.footer}>
        <button
          type="button"
          onClick={() => (window.location.href = "/portail-medecin")}
          style={styles.btn("primary", false)}
        >
          ← Retour à l&apos;Espace médecins
        </button>

        <button
          type="button"
          onClick={handleValidate}
          style={styles.btn("primary", isLocked)}
          disabled={isLocked}
        >
          Enregistrer mes souhaits
        </button>
      </div>
    </div>
  );
};

export default DesiderataGardePortRoyal;
